import { useMemo } from 'react';
import { useFetchData } from '../hooks/useFetchData';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { v2ServicePath } from '../config';

// ── normalizers ────────────────────────────────────────────────────────────

function normalizeV2Model(m) {
  const calls = m.call_count ?? 0;
  const errorCount = m.error_count ?? 0;
  const avgMs = m.avg_duration_ms ?? null;
  const inputTokens = m.total_input_tokens ?? 0;
  const outputTokens = m.total_output_tokens ?? 0;
  return {
    id: String(m.model_id),
    name: m.model_name,
    provider: m.llm_provider ?? null,
    calls,
    total_duration: avgMs != null ? avgMs * calls : null,
    error_rate: calls > 0 ? Math.round((errorCount / calls) * 10000) / 100 : 0,
    prompt_tokens: inputTokens,
    completion_tokens: outputTokens,
    total_tokens: inputTokens + outputTokens,
    cost: 0,
    agents: Array.isArray(m.agent_names) ? m.agent_names : [],
    security_alerts: 0,
    last_seen: m.last_seen ?? null,
  };
}

// ── hook ───────────────────────────────────────────────────────────────────

/**
 * Fetch and normalize the models list for a service.
 */
export function useModelList(selectedAppId) {
  const { selectedEnvironment } = useEnvironment();

  const endpoint = useMemo(() => {
    if (!selectedAppId) return null;
    return v2ServicePath(selectedAppId, '/models');
  }, [selectedAppId]);

  const { data, loading, error } = useFetchData({
    endpoint,
    enabled: !!selectedAppId && !!selectedEnvironment,
  });

  const models = useMemo(() => {
    if (!data) return [];
    return (Array.isArray(data.data) ? data.data : []).map(normalizeV2Model);
  }, [data]);

  return { models, loading, error };
}
